/**
 * Environment variables configuration
 *
 * This file provides type-safe access to environment variables.
 * It validates that required environment variables are present
 * and provides default values for optional ones.
 *
 * All environment variables use the VITE_ prefix for Vite compatibility.
 */

// Define the shape of the environment variables
type Env = {
  // API configuration
  API_URL: string;
  AGENTS_API_URL: string;
  // Base URL for long-lived streaming endpoints (e.g., Scribe SSE chat).
  // Should route through the public ALB, not API Gateway — API GW has a
  // 30-second idle timeout that cuts off long streams.
  STREAMING_API_URL: string;

  // Feature flags
  ENABLE_PRODUCT_OBSERVABILITY: boolean;
  ENABLE_MULTI_TENANT: boolean;

  // Application configuration
  APP_NAME: string;
  APP_ENV: 'local-cloud' | 'development' | 'staging' | 'prod';

  // Maximum upload size in bytes (default: 50MB)
  MAX_UPLOAD_SIZE: number;

  // White-label build: 'aes' turns MATIC into the AES Quoting app (Otto-mode
  // rail only, Quotes entry, no QMS toggle). Empty for the stock product.
  // The deployment's brand before anyone signs in (app name, loading mark). What a signed-in
  // tenant sees follows its own quoting engine, not this (useQuotesFirst).
  WHITE_LABEL: 'aes' | '';
  // Otto's generated Home suggestions (an LLM call per visit). Off unless set to 'true'.
  HOME_RECOMMENDATIONS_ENABLED: boolean;
  // 'cognito' (default) | 'local' (mock user) | 'local-jwt' (password login)
  AUTH_MODE: string;
};

// Helper to get Vite environment variables
const getEnv = (key: string): string | undefined => {
  if (import.meta?.env) {
    // Access Vite env var with VITE_ prefix
    return import.meta.env[`VITE_${key}`] as string | undefined;
  }

  return undefined;
};

// An env var that was set but empty (a Docker build arg left blank) reads as unset.
const nonEmpty = (value: string | undefined): string | undefined =>
  value === undefined || value === '' ? undefined : value;

// Same-origin default: the single-container build serves the SPA and the API from
// one host, so a blank VITE_API_URL at build time means "this page's origin".
const sameOrigin = (): string =>
  globalThis.window === undefined
    ? 'http://localhost:8000'
    : globalThis.location.origin;

const appEnv = (getEnv('APP_ENV') as Env['APP_ENV']) ?? 'development';
const productObservabilityFlag = getEnv('ENABLE_PRODUCT_OBSERVABILITY');

// Get environment variables with defaults
export const env: Env = {
  // API configuration
  API_URL: nonEmpty(getEnv('API_URL')) ?? sameOrigin(),
  AGENTS_API_URL:
    nonEmpty(getEnv('AGENTS_API_URL')) ??
    nonEmpty(getEnv('API_URL')) ??
    sameOrigin(),
  STREAMING_API_URL:
    nonEmpty(getEnv('STREAMING_API_URL')) ??
    nonEmpty(getEnv('AGENTS_API_URL')) ??
    nonEmpty(getEnv('API_URL')) ??
    sameOrigin(),

  // Feature flags
  ENABLE_PRODUCT_OBSERVABILITY: productObservabilityFlag !== 'false',
  ENABLE_MULTI_TENANT: getEnv('ENABLE_MULTI_TENANT') === 'true',

  // Application configuration
  APP_NAME: getEnv('APP_NAME') ?? 'MATIC',
  APP_ENV: appEnv,

  // Maximum upload size (default: 50MB)
  MAX_UPLOAD_SIZE: Number.parseInt(getEnv('MAX_UPLOAD_SIZE') ?? '52428800', 10),

  WHITE_LABEL: getEnv('WHITE_LABEL') === 'aes' ? 'aes' : '',
  HOME_RECOMMENDATIONS_ENABLED:
    getEnv('HOME_RECOMMENDATIONS_ENABLED') === 'true',
  AUTH_MODE: (getEnv('AUTH_MODE') ?? 'cognito').toLowerCase(),
};

// Validate required environment variables
const validateEnv = () => {
  const requiredEnvVars: Array<keyof Env> = ['API_URL'];

  const missingEnvVars = requiredEnvVars.filter((key) => !env[key]);

  if (missingEnvVars.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missingEnvVars.map((k) => `VITE_${k}`).join(', ')}`
    );
  }
};

// Only validate in browser environment to avoid issues during build
if (globalThis.window !== undefined) {
  validateEnv();
}

export default env;
